import { NamedModel, Result, Suggestion } from '@empathy/search-types';
import { Observable } from 'rxjs/Observable';
import { distinctUntilChanged } from 'rxjs/operators';
import { debounceTime } from 'rxjs/operators/debounceTime';
import { map } from 'rxjs/operators/map';
import { EVENTS } from '../../bus/events.const';
import { EmpathizeLayoutItem, EmpathizeState, EmpathyXConfig } from '../../models';
import { ACTIONS_KEYS, GETTERS_KEYS, MUTATIONS_KEYS } from '../../store';
import { isArrayEmptyOrNull } from '../../utils';
import { createWires } from '../wiring.util';
import { WIRE_FACTORY } from './wires.factory';

export const EMPATHIZE_WIRES = createWires({
  getTermSuggestions(observable, { store, emit }) {
    const config: EmpathyXConfig = store.getters[GETTERS_KEYS.config.config];
    const searchRequest$ = observable
      .pipe(
        debounceTime(config.empathize.debounceTimeInMs),
        map(() => store.state.core.inputQuery),
        distinctUntilChanged((lastQuery: string, newQuery: string) =>
          lastQuery.trim() === newQuery.trim()
        )
      );
    searchRequest$.subscribe(() => {
      store.commit(MUTATIONS_KEYS.empathize.setLoading, true);
      store.dispatch(ACTIONS_KEYS.empathize.getTermSuggestions)
        .then((empathizeState: EmpathizeState) => {
          if (empathizeState) {
            emit(EVENTS.EmpathizeSuccess, empathizeState);
          }
          store.commit(MUTATIONS_KEYS.empathize.setLoading, false);
        });
    });
  },
  getIdentifierSuggestions(observable, { store }) {
    const config: EmpathyXConfig = store.getters[GETTERS_KEYS.config.config];
    const identifierRegex = new RegExp(config.identifierSuggestions.regex.identifierDetection);
    observable
      .pipe(debounceTime(config.empathize.debounceTimeInMs))
      .subscribe(() => {
        if (identifierRegex.test(store.state.core.inputQuery)) {
          store.dispatch(ACTIONS_KEYS.empathize.getIdentifierSuggestions);
        }
      });
  },
  suggestionSelected(observable: Observable<Suggestion>, { store, emit }) {
    observable.subscribe((suggestion: Suggestion) => {
      if (!isArrayEmptyOrNull(suggestion.facets)) {
        const suggestionFilter = suggestion.facets[0].filters[0];
        store.dispatch(ACTIONS_KEYS.search.clearAllFiltersAndSelectOne, suggestionFilter);
      }
      emit(EVENTS.QuerySet, suggestion.term);
    });
  },
  empathizeArrowPress(observable: Observable<string | number>, { store }) {
    observable.subscribe(key => {
      if (key === 'ArrowLeft' || key === 37) {
        store.dispatch(ACTIONS_KEYS.empathize.decreaseSelectedIndexX);
      } else if (key === 'ArrowUp' || key === 38) {
        store.dispatch(ACTIONS_KEYS.empathize.decreaseSelectedIndexY);
      } else if (key === 'ArrowRight' || key === 39) {
        store.dispatch(ACTIONS_KEYS.empathize.increaseSelectedIndexX);
      } else if (key === 'ArrowDown' || key === 40) {
        store.dispatch(ACTIONS_KEYS.empathize.increaseSelectedIndexY);
      }
    });
  },
  selectHighlightedEmpathizeSuggestion(observable, { store, emit }) {
    observable.subscribe(() => {
      const suggestion: NamedModel = store.getters[GETTERS_KEYS.empathize.highlightedEmpathizeItem];
      if (suggestion) {
        const layoutItem: EmpathizeLayoutItem = store.getters[GETTERS_KEYS.empathize.highlightedEmpathizeLayoutItem];
        emit(layoutItem.itemSelectedEvent, suggestion);
        store.dispatch(ACTIONS_KEYS.empathize.clearSelectedIndex);
        if (suggestion.modelName === 'Result') {
          window.location.href = (suggestion as Result).url;
        }
      }
    });
  },
  openEmpathize: WIRE_FACTORY.commitMutation<boolean>(MUTATIONS_KEYS.core.setOpenEmpathize, true),
  closeEmpathize: WIRE_FACTORY.commitMutation<boolean>(MUTATIONS_KEYS.core.setOpenEmpathize, false),
  getPopularSearches: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.empathize.getPopularSearches),
  getResultsSuggestions: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.empathize.getResultsSuggestions),
  empathizeElementSelected: WIRE_FACTORY.emitEvent(EVENTS.EmpathizeElementSelected),
  saveEmpathizeResponse: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.empathize.saveEmpathizeResponse),
  clearTermSuggestions: WIRE_FACTORY.commitMutation<Partial<EmpathizeState>>(MUTATIONS_KEYS.empathize.saveEmpathizeResponse,
    { termSuggestions: [] })
});
